/**
 * Company: SPHMedia
 * Description: [Mata Mata landing page banner]
 */

import BannerBg from "@assets/ugcBanner/banner-bg.jpg";
import BannerLogo from "@assets/ugcBanner/banner-logo.svg";
import BannerLogoMobile from "@assets/ugcBanner/banner-logo-mobile.svg";
import CloseIcon from "@assets/ugcBanner/cross-icon.svg";
import EmailIcon from "@assets/ugcBanner/email-icon.svg";
import WhatsAppIcon from "@assets/ugcBanner/whatsapp-icon.svg";
import Link from "@elements/Typography/Link";
import {
  CD_LANGUAGE,
  useBHCustomEventAnalyticsCall,
} from "@hooks/useAnalyticsCall/useBHCustomEventAnalyticsCall";
import Heading from "@sphtech/sph-web-ui/elements/Heading";
import { ReactElement, useState } from "react";

import { CTA_CONFIG } from "./helpers/mataMata.config";
import {
  PRIVACY_POLICY_LINK,
  TERMS_AND_CONDITIONS_LINK,
} from "./helpers/mataMata.constants";
import { CTAType } from "./helpers/mataMata.types";

/**
 * MataMataBannerWithCTA
 * Description: [Mata-Mata landing page banner with whatsapp and email cta]
 * @param props - The props required by the component (if any)
 * @returns The rendered React element
 */
export default function MataMataBannerWithCTA(): ReactElement {
  const bhCustomEvent = useBHCustomEventAnalyticsCall();
  const [showModal, setShowModal] = useState(false);
  const [ctaType, setCtaType] = useState<CTAType | null>(null);

  const closeModal = () => {
    setShowModal(false);
    setCtaType(null);
  };

  const trackEvent = (
    label: string,
    actionLabel: string,
    language = CD_LANGUAGE.MALAY,
  ) => {
    bhCustomEvent(label, "click", actionLabel, undefined, undefined, language);
  };

  const handleCTAButtonClick = (label: CTAType) => {
    setShowModal(true);
    setCtaType(label);

    trackEvent(label, CTA_CONFIG[label].openLabel);
  };

  const linkClickEvent = (label: string) => {
    trackEvent(label, "(not set)", CD_LANGUAGE.ENGLISH);
  };

  const handleCancel = () => {
    if (!ctaType) {
      return;
    }

    trackEvent(ctaType, CTA_CONFIG[ctaType].cancelLabel);

    closeModal();
  };

  const handleAgree = () => {
    if (!ctaType) {
      return;
    }

    CTA_CONFIG[ctaType].open();

    trackEvent(ctaType, CTA_CONFIG[ctaType].agreeLabel);

    closeModal();
  };

  return (
    <>
      <div
        className="flex flex-col lg:flex-row w-full lg:max-w-[1167px]"
        style={{
          backgroundImage: `url(${BannerBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="flex flex-grow-no-shrink justify-center items-center">
          <picture className="w-[200px] md:w-[235px] lg:w-[252px] my-[29px] md:my-[19px] lg:my-0">
            <source srcSet={BannerLogo} media="(min-width: 768px)" />
            <img src={BannerLogoMobile} alt="Mata Mata Logo" />
          </picture>
        </div>
        <div className="w-full lg:w-[65%] flex flex-col gap-2.5 md:gap-[15px] py-2.5 px-[15px] md:py-[11px] md:px-[14px] bg-neutral-0/90">
          <Heading
            Element="h3"
            className="text-red-900 text-[30px] leading-[120%] font-bold"
          >
            Kongsi berita, foto dan video dengan kami!
          </Heading>
          <div className="flex flex-col gap-2.5 md:gap-[12px]">
            <span className="text-[21px] md:text-[26px] text-neutral-1000 leading-[120%] font-bold">
              &apos;Mata-mata&apos; wadah bagi netizen berkongsi berita menarik
              dengan BH
            </span>
            <p className="text-[17px] leading-[150%] font-normal text-[#181818] pt-1">
              Ada kejadian menarik perhatian anda? Hantar berita, foto dan video
              menerusi salah satu cara ini: Muat naik di akaun media sosial anda
              (pastikan tidak privet) dengan hashtag #bhmata di TikTok,
              Instagram atau Facebook, serta tag kami di @beritahariansg atau
              Berita Harian Singapura di Facebook.
            </p>
            <div className="flex gap-2.5 items-center pb-1.5">
              <button
                data-testid="mata-mata-whatsapp-btn"
                className="flex items-center gap-2.5 rounded-[30px] bg-green-500 px-5 h-[40px]"
                onClick={() => {
                  handleCTAButtonClick("whatsapp");
                }}
              >
                <img src={WhatsAppIcon} alt="whatsapp" width={29} height={29} />
                <span className="text-neutral-0 text-base leading-[120%] font-bold whitespace-nowrap">
                  WhatsApp kami
                </span>
              </button>
              <button
                data-testid="mata-mata-email-btn"
                className="flex items-center gap-2.5 rounded-[30px] bg-blue-100 px-5 h-[40px]"
                onClick={() => {
                  handleCTAButtonClick("email");
                }}
              >
                <img src={EmailIcon} alt="email" width={22} height={18} />
                <span className="text-neutral-0 text-base leading-[120%] font-bold whitespace-nowrap">
                  E-mel kami
                </span>
              </button>
            </div>
            <div>
              <p className="text-xs leading-[135%] font-normal">
                <Link
                  testId="mata-mata-terms-link"
                  destination={TERMS_AND_CONDITIONS_LINK}
                  relativePath={false}
                  target="_blank"
                  className="text-blue-600"
                  handleClick={() => {
                    linkClickEvent("terms and conditions");
                  }}
                >
                  Syarat dan Peraturan
                </Link>
                <span className="text-[#181818]"> dan </span>
                <Link
                  testId="mata-mata-privacy-link"
                  destination={PRIVACY_POLICY_LINK}
                  relativePath={false}
                  target="_blank"
                  className="text-blue-600"
                  handleClick={() => {
                    linkClickEvent("privacy policy");
                  }}
                >
                  Kenyataan Privasi
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
      {showModal && (
        <div
          data-testid="mata-mata-consent-modal"
          className="flex fixed top-0 left-0 w-full h-full bg-neutral-1000/50 z-[9999] justify-center items-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="mata-mata-consent-title"
        >
          <button
            type="button"
            aria-label="Close modal"
            className="absolute inset-0 cursor-default"
            onClick={closeModal}
          />
          <div className="relative flex flex-col w-full max-w-[480px] py-4 bg-neutral-0 shadow-[0px_0px_8px_2px_rgba(50, 50, 58, 0.20)]">
            {/* Screen reader only content */}
            <h2 id="mata-mata-consent-title" className="sr-only">
              Mata Mata submission consent
            </h2>
            <div className="pt-2 pr-12 pb-6 pl-6 text-base font-medium text-[#1a1a23]">
              By proceeding, I confirm that I have read and agree to the{" "}
              <Link
                destination={TERMS_AND_CONDITIONS_LINK}
                target="_blank"
                relativePath={false}
                className="text-blue-600 underline"
              >
                Terms and Conditions
              </Link>{" "}
              and{" "}
              <Link
                destination={PRIVACY_POLICY_LINK}
                target="_blank"
                relativePath={false}
                className="text-blue-600 underline"
              >
                Privacy Policy
              </Link>{" "}
              displayed on the submission banner.
              <br />
              <br />
              Dengan meneruskan langkah selanjutnya, saya mengesahkan saya telah
              membaca dan bersetuju dengan{" "}
              <Link
                destination={TERMS_AND_CONDITIONS_LINK}
                target="_blank"
                relativePath={false}
                className="text-blue-600 underline"
              >
                Terma dan Syarat
              </Link>{" "}
              serta{" "}
              <Link
                destination={PRIVACY_POLICY_LINK}
                target="_blank"
                relativePath={false}
                className="text-blue-600 underline"
              >
                Dasar Privasi
              </Link>{" "}
              yang dipaparkan dalam rentang penghantaran.
            </div>
            <div className="flex px-6 justify-end items-center gap-2.5 text-xs font-regular">
              <button
                data-testid="mata-mata-cancel-btn"
                className="flex justify-center items-center min-w-[166px] h-[52px] p-[25px] border border-solid border-blue-400 bg-neutral-0 text-blue-400"
                onClick={handleCancel}
              >
                Batal
              </button>
              <button
                data-testid="mata-mata-agree-btn"
                className="flex justify-center items-center min-w-[166px] h-[52px] p-[25px] border border-solid border-blue-400 bg-blue-400 text-[#FAFAFA]"
                onClick={handleAgree}
              >
                Sah dan setuju
              </button>
            </div>
            <div className="absolute top-3 right-3">
              <button
                type="button"
                aria-label="close"
                className="bg-neutral-0 border-0"
                onClick={closeModal}
              >
                <img src={CloseIcon} alt="close" width={24} height={24} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
